import React, { useCallback, useEffect, useState } from 'react';
import { Eye, RefreshCw, X } from 'lucide-react';
import { dynamicFieldsApi } from './api';
import { LibraryRef, PreviewField } from './types';
import { iconButton, inputClass, secondaryButton, Skeleton } from '../ui';

const INPUT_TYPES: Record<string, string> = { number: 'number', email: 'email', url: 'url', tel: 'tel', date: 'date', time: 'time', datetime: 'datetime-local' };

const errorText = (e: any) => {
  const detail = e?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message) || e?.message || 'Preview failed';
};

/** Read-only control, so the preview shows what the step would render without being fillable. */
function PreviewControl({ field }: { field: PreviewField }) {
  const required = field.validation.some((rule) => rule.type === 'required');
  const common = { disabled: true, 'aria-label': field.label, placeholder: field.placeholder || undefined, className: `${inputClass()} !mt-1` };
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[12px] font-medium text-slate-700">{field.label}{required && <span className="ml-0.5 text-red-500" title="Required">*</span>}</span>
        <code className="shrink-0 text-[9px] text-slate-400">{field.key}</code>
      </div>
      {field.type === 'textarea' ? <textarea rows={2} {...common} />
        : field.type === 'select' ? <select {...common}><option /></select>
        : <input type={INPUT_TYPES[field.type] || 'text'} {...common} />}
    </div>
  );
}

type LibraryPreviewDialogProps = {
  library: LibraryRef;
  /** Published version being previewed, shown in the header. */
  version?: number | null;
  onClose: () => void;
};

/**
 * Preview of a library as users will see it, without opening the editor.
 * It calls the endpoint a running form uses, so the keys (including range-scaled points) are the real ones.
 */
export default function LibraryPreviewDialog({ library, version, onClose }: LibraryPreviewDialogProps) {
  const [range, setRange] = useState('50mm');
  const [fields, setFields] = useState<PreviewField[]>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback((forRange: string) => {
    setBusy(true);
    setError(undefined);
    dynamicFieldsApi.preview(library, forRange)
      .then(setFields)
      .catch((e) => { setFields(undefined); setError(errorText(e)); })
      .finally(() => setBusy(false));
  }, [library]);

  useEffect(() => { load('50mm'); }, [load]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-[2px]">
      <div role="dialog" aria-modal="true" aria-labelledby="library-preview-title" className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
          <div>
            <h2 id="library-preview-title" className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Eye className="h-4 w-4 text-primary-600" /> {library.sensor_type} <span className="text-slate-300">/</span> {library.category}
              {version != null && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Live v{version}</span>}
            </h2>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">The fields a form receives for this sensor type and category. Read-only: nothing here is saved. The sensor range decides the calibration point keys.</p>
          </div>
          <button type="button" className={iconButton} onClick={onClose} aria-label="Close preview"><X className="h-3.5 w-3.5" /></button>
        </div>

        <form className="flex items-end gap-2 border-b border-slate-100 px-4 py-3" onSubmit={(e) => { e.preventDefault(); load(range); }}>
          <label className="text-[11px] text-slate-600">Sensor range
            <input className={`${inputClass()} !mt-0.5 h-8 w-36`} value={range} onChange={(e) => setRange(e.target.value)} placeholder="e.g. 50mm" />
          </label>
          <button type="submit" className={secondaryButton} disabled={busy}><RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Update</button>
          {fields && <span className="ml-auto pb-2 text-[11px] text-slate-500">{fields.length} field{fields.length === 1 ? '' : 's'}</span>}
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {busy && !fields && <div className="grid gap-3 sm:grid-cols-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>}
          {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-700">{error}</p>}
          {fields && fields.length === 0 && <p className="py-8 text-center text-[12px] text-slate-400">The live library returns no fields for this range.</p>}
          {fields && fields.length > 0 && (
            <div className={`grid gap-x-4 gap-y-3 sm:grid-cols-2 ${busy ? 'opacity-60' : ''}`}>
              {fields.map((field) => <PreviewControl key={field.key} field={field} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
